import { useEffect, useMemo, useState } from "react";
import usePartAssembly from "../../../../data/usePartAssembly";
import { CategoryWithParts, ILeadPartsTableGlobalFilters, IPart, IPartAssembly } from "../../../../models/Part";
import { PartCategoryConflictMap } from "../../../../models/PartCategorySubPart";
import LoadingWheel from "../../../shared/LoadingWheel";
import { IPartAssemblyPartCategories } from '../../../../models/Part';
import LeadPartsCategoriesTable from "./LeadPartsCategoriesTable";
import { IVehicle } from "../../../../models/Vehicle";
import { CheckFeatureFlag } from "../../../../hooks/useFeatureFlag";

export interface PartAssemblyWithMatchingCategories extends IPartAssembly {
  matchingCategories: CategoryWithParts[];
}

export interface LeadPartsCategoriesProps {
  categories: CategoryWithParts[];
  handleTableSelectionChange: (tableId: string, update: CategoryWithParts[] | ((prev: CategoryWithParts[]) => CategoryWithParts[])) => void;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
  filter?: (cat: CategoryWithParts) => boolean;
  leadPartsTableFilters: ILeadPartsTableGlobalFilters;
  showBestPrice?: boolean;
  /** Global (cross-table) main/sub-part clashes, keyed by category id. */
  categoryConflicts?: PartCategoryConflictMap;
  /** Sub-part ids the TME auto-select must skip. `undefined` while the link data loads. */
  autoSelectSuppressedCategoryIds?: Set<string>;
  /**
   * For a category listed by more than one assembly, the one assembly whose table may
   * auto-select it. Decided in CreateJobs, where the prices that rule turns on live.
   */
  autoSelectOwnerAssemblyIdByCategoryId?: Map<string, string>;
  /**
   * The per-table selection state from CreateJobs. Each assembly table is driven by its own
   * slice rather than by the global `selectedCategories`: a category can belong to two
   * assemblies, and showing it ticked in both tables made any later click in the second table
   * write it into that table's selection as well - a duplicate part.
   */
  selectedByTableId: Record<string, CategoryWithParts[]>;
};

export default function LeadPartsCategories({
  categories,
  handleTableSelectionChange,
  partProUnlocked,
  partProLoading,
  vehicle,
  filter,
  leadPartsTableFilters,
  showBestPrice = false,
  categoryConflicts,
  autoSelectSuppressedCategoryIds,
  autoSelectOwnerAssemblyIdByCategoryId,
  selectedByTableId,
}: LeadPartsCategoriesProps) {
  const { assemblies } = usePartAssembly();
  const hasTmeFeature = CheckFeatureFlag("TME");
  const [isLoading, setIsLoading] = useState(true);
  const [categoryAssemblies, setCategoryAssemblies] = useState<PartAssemblyWithMatchingCategories[] | undefined>([]);
  const [unmatchedCategories, setUnmatchedCategories] = useState<CategoryWithParts[] | undefined>([]);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const getUniqueCategories = (incoming: CategoryWithParts[]) => {
    const byCategoryId = new Map<string, CategoryWithParts>();
    incoming.forEach((category) => {
      if (!byCategoryId.has(category.category.id)) {
        byCategoryId.set(category.category.id, category);
      }
    });
    return [...byCategoryId.values()];
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!assemblies.isLoading && categories) {
      const uniqueCategories = getUniqueCategories(categories);

      //Find matching categories for each assembly and unmatched categories
      const matchedAssemblies: PartAssemblyWithMatchingCategories[] = assemblies.data?.data.map((assembly: IPartAssembly) => {
        // Create a map of categoryId to order for quick lookup
        const categoryOrderMap = new Map(
          assembly.partAssemblyPartCategories?.map((cat, index) => [cat.partCategoryId, index]) ?? []
        );

        // Filter and sort categories based on the order in partAssemblyPartCategories
        const matchingCategories: CategoryWithParts[] = uniqueCategories
          .filter(category =>
            assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) => cat.partCategoryId === category.category.id)
          )
          .sort((a, b) => {
            const orderA = categoryOrderMap.get(a.category.id) ?? Number.MAX_SAFE_INTEGER;
            const orderB = categoryOrderMap.get(b.category.id) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

        return {
          ...assembly,
          matchingCategories,
        };
      }) ?? [];


      // Filter out categories that have no matching assemblies
      const unmatchedCategories = uniqueCategories.filter(category =>
        !matchedAssemblies?.some(assembly =>
          assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) => cat.partCategoryId === category.category.id)
        )
      );

      // console.log("Matched Assemblies", matchedAssemblies);
      // console.log("Unmatched Categories", unmatchedCategories);

      setCategoryAssemblies(matchedAssemblies);
      setUnmatchedCategories(unmatchedCategories);

      // setPartAssemblies(assemblies.data);
      setIsLoading(false);
    }
  }, [assemblies.isLoading, categories]);

  /**
   * Per-assembly auto-select suppression: the global sub-part suppression, plus every category
   * this table shows that another assembly owns. A category listed by two assemblies appears in
   * both of their tables, so all but the owning one must skip it - otherwise it is auto-selected
   * twice and created as two parts. The operator can still tick it in both by hand; that's a
   * deliberate second part, and SelectedCategories warns about it.
   *
   * Returns `undefined` while `autoSelectSuppressedCategoryIds` is still loading, so the
   * tables keep holding off exactly as they did before.
   */
  const autoSelectSuppressedByAssemblyId = useMemo<Record<string, Set<string>> | undefined>(() => {
    if (!autoSelectSuppressedCategoryIds) return undefined;

    const byAssemblyId: Record<string, Set<string>> = {};
    categoryAssemblies?.forEach((assembly) => {
      const suppressed = new Set(autoSelectSuppressedCategoryIds);
      assembly.matchingCategories.forEach((cat) => {
        const ownerAssemblyId = autoSelectOwnerAssemblyIdByCategoryId?.get(cat.category.id);
        if (ownerAssemblyId && ownerAssemblyId !== assembly.id) suppressed.add(cat.category.id);
      });
      byAssemblyId[assembly.id] = suppressed;
    });

    return byAssemblyId;
  }, [categoryAssemblies, autoSelectSuppressedCategoryIds, autoSelectOwnerAssemblyIdByCategoryId]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div>

      {categoryAssemblies && categoryAssemblies.length > 0 && (
        categoryAssemblies.map((matchedAssembly: PartAssemblyWithMatchingCategories) => {

          // Only this table's own selection, not the global one - see `selectedByTableId`.
          const selectedForThisTable = selectedByTableId[matchedAssembly.id] ?? [];
          const preselectedForThisAssembly = matchedAssembly.matchingCategories.filter(cat =>
            selectedForThisTable.some(sel => sel.category.id === cat.category.id)
          );

          return (matchedAssembly.matchingCategories.length > 0 && (
            <div key={matchedAssembly.id} className="first:mt-0 mt-8">
              <h2 className="font-semibold">
                <button
                  type="button"
                  className="flex items-center gap-x-2 cursor-pointer hover:text-primary-600 select-none"
                  onClick={() => toggleSection(matchedAssembly.id)}
                >
                  <span
                    className={`transform transition-transform duration-200 ${collapsedSections.has(matchedAssembly.id) ? "rotate-0" : "rotate-90"
                      }`}
                  >
                    ▶
                  </span>
                  {matchedAssembly.name}
                </button>
              </h2>

              {!collapsedSections.has(matchedAssembly.id) && (
                <LeadPartsCategoriesTable
                  categories={matchedAssembly.matchingCategories}
                  selectedCategories={preselectedForThisAssembly}
                  setSelectedCategories={(update) => handleTableSelectionChange(matchedAssembly.id, update)}
                  partProUnlocked={partProUnlocked}
                  partProLoading={partProLoading}
                  vehicle={vehicle}
                  filter={filter}
                  leadPartsTableFilters={leadPartsTableFilters}
                  showBestPrice={showBestPrice}
                  autoSelect={hasTmeFeature}
                  categoryConflicts={categoryConflicts}
                  autoSelectSuppressedCategoryIds={autoSelectSuppressedByAssemblyId?.[matchedAssembly.id]}
                />
              )}
            </div>
          ))
        })
      )}

      {categoryAssemblies && unmatchedCategories && unmatchedCategories?.length > 0 && (() => {
        const selectedForUnmatched = selectedByTableId["unmatched"] ?? [];
        const preselectedForUnmatched = unmatchedCategories.filter(cat =>
          selectedForUnmatched.some(sel => sel.category.id === cat.category.id)
        );

        return (<div className="mt-8">
          <h2 className="font-semibold">
            <button
              type="button"
              className="flex items-center gap-x-2 cursor-pointer hover:text-primary-600 select-none"
              onClick={() => toggleSection("unmatched")}
            >
              <span
                className={`transform transition-transform duration-200 ${collapsedSections.has("unmatched") ? "rotate-0" : "rotate-90"
                  }`}
              >
                ▶
              </span>
              Non-Assembly Parts
            </button>
          </h2>
          {!collapsedSections.has("unmatched") && (
            <LeadPartsCategoriesTable
              categories={unmatchedCategories ?? []}
              selectedCategories={preselectedForUnmatched}
              setSelectedCategories={(update) => handleTableSelectionChange("unmatched", update)}
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
              vehicle={vehicle}
              filter={filter}
              leadPartsTableFilters={leadPartsTableFilters}
              showBestPrice={showBestPrice}
              categoryConflicts={categoryConflicts}
            />
          )}
        </div>
        )
      })()}
    </div>
  );
}